/**
 * MfaBackupStep — the backup codes, shown plain ONCE, and the person's acknowledgement that they saved them.
 *
 * The enrolment wizard's last step, and the passkey card's answer when a first passkey turned MFA on
 * (ECOSYSTEM-EMAIL-001 Q-EE-34 P5 unit 5): promoted out of `MfaSetupWizard`, unchanged, so both say the same thing.
 *
 * @module @puffin-core/mfa-ui/internal/MfaBackupStep
 */

import PropTypes from 'prop-types';
import { useMfaTranslate } from '../hooks/useMfaTranslate.js';

/**
 * @param {Object} props
 * @param {string[]} [props.backupCodes] - the codes, shown plain this once
 * @param {boolean} props.acknowledged - whether the person ticked that they saved them
 * @param {React.ChangeEventHandler<HTMLInputElement>} props.onAcknowledgeChange - the "I saved them" checkbox
 * @param {() => void} props.onComplete - Continue, once the codes are acknowledged
 * @param {string} [props.introKey='setup_backup_intro'] - the `mfa` leaf that says why the codes appeared
 * @param {Function} [props.t] - a consumer translator for the `mfa` corpus
 * @returns {React.JSX.Element}
 */
export function MfaBackupStep({
  backupCodes,
  acknowledged,
  onAcknowledgeChange,
  onComplete,
  introKey = 'setup_backup_intro',
  t,
}) {
  const str = useMfaTranslate(t);

  return (
    <div>
      <h3 className="mfa-challenge-title">{str('setup_title_backup')}</h3>
      <p className="mfa-challenge-subtitle">{str(introKey)}</p>

      <div className="mfa-backup-codes" data-testid="mfa-backup-codes">
        {backupCodes &&
          backupCodes.map((backupCode) => (
            <div key={backupCode} className="mfa-backup-code">
              {backupCode}
            </div>
          ))}
      </div>

      <div className="form-group">
        <label className="form-label">
          <input type="checkbox" checked={acknowledged} onChange={onAcknowledgeChange} />{' '}
          {str('setup_backup_ack')}
        </label>
      </div>

      <div className="mfa-setup-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onComplete}
          disabled={!acknowledged}
        >
          {str('setup_btn_complete')}
        </button>
      </div>
    </div>
  );
}

MfaBackupStep.propTypes = {
  backupCodes: PropTypes.arrayOf(PropTypes.string),
  acknowledged: PropTypes.bool.isRequired,
  onAcknowledgeChange: PropTypes.func.isRequired,
  onComplete: PropTypes.func.isRequired,
  introKey: PropTypes.string,
  t: PropTypes.func,
};
